import { Tooltip, TooltipContent, TooltipTrigger, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { translateUserStatusVisual } from "@/features/identity-hub/utils/status";

interface UserStatusBadgeProps {
  status: string;
  /** Overrides `cfg.label` — used by the Person drawer, which prefers its own i18n key. */
  label?: string;
  /** "tooltip" (table rows) wraps the pill with a `cfg.desc` tooltip. "plain" (drawer header) renders the bare pill. */
  variant?: "tooltip" | "plain";
}

/**
 * User status pill (active/pending/inactive/deactivated). Single source for
 * what used to be a `StatusBadge` copy in the People table and a `StatusPill`
 * copy in the Person drawer header — both rendered the exact same markup.
 */
export function UserStatusBadge({ status, label, variant = "tooltip" }: UserStatusBadgeProps) {
  const { t } = useTranslation();
  const cfg = translateUserStatusVisual(t, status);
  const pill = (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium", cfg.pill, cfg.text, cfg.border)}>
      <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", cfg.dot)} />
      {label ?? cfg.label}
    </span>
  );

  if (variant === "plain") return pill;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{pill}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px]">{cfg.desc}</TooltipContent>
    </Tooltip>
  );
}
